import './style.scss';

import { DeleteOutlined, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Button } from '@components/Button/Button';
import { Modal } from '@components/common/Modal/Modal';
import { PageTitle } from '@components/common/PageTitle/PageTitle';
import { UTable } from '@components/common/UTable/UTable';
import { Card as AntCard, Modal as AntModal, Typography } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import {
  useGetDictionariesQuery,
  useLazyGetMedicineRecordQuery,
} from '@/api/dictionaries/api';
import {
  useDeleteBatchMutation,
  useDeleteDrugMutation,
  useExpenseBatchMutation,
  useGetDrugsQuery,
  useLazyGetDrugExpenseQuery,
  usePersistMedicineBatchMutation,
  useUpdatePackagesAmountMutation,
} from '@/api/pharmacy/api';
import { useHasWithUnexpensedDrugsQuery } from '@/api/vaccination/task/api';
import { columns as drugInfoColumns } from '@/pages/Pharmacy/components/card/table.config';
import { ExpenseModalContent } from '@/pages/Pharmacy/components/Monitoring/ExpenseModalContent';
import {
  createBatchModalConfig,
  createModalConfig,
  deleteBatchModalConfig,
} from '@/pages/Pharmacy/components/Monitoring/modal.config';
import { OrganizationLevelType } from '@/shared/organizations/enums';
import { hasPermission } from '@/shared/security/hasPermission';
import { pharmacy } from '@/shared/security/permissions/pharmacy';
import { withSecurity } from '@/shared/security/withSecurity';
import { mapTableIndexWithPage } from '@/utils/utils';

import { columns } from './columns.config';

export const Monitoring = withSecurity(({ auth }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const orgId = searchParams.get('organizationId') || '';

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDrugInfoModalOpen, setIsDrugInfoModalOpen] = useState(false);
  const [isBatchCreateModalOpen, setIsBatchCreateModalOpen] = useState(false);
  const [deleteBatchModal, setDeleteBatchModal] = useState(false);
  const [deleteDrugModal, setDeleteDrugModal] = useState(false);
  const [expenseModalOpen, setExpenseModalOpen] = useState(false);

  const [drugsInternal, setDrugsInternal] = useState<any[]>([]);

  const [createBatchDrug, setCreateBatchDrug] = useState<any | null>(null);
  const [deleteBatchDrugId, setDeleteBatchDrugId] = useState<string | null>(null);
  const [deleteDrugId, setDeleteDrugId] = useState<any | null>(null);

  const { data: hasExpense } = useHasWithUnexpensedDrugsQuery();

  const { data: drugs, isLoading } = useGetDrugsQuery(
    { filters: { organizationId: orgId } },
    { skip: !orgId },
  );

  const [getExpense, { data: expense, isLoading: isExpenseLoading }] =
    useLazyGetDrugExpenseQuery();

  const { data: dictionaries, isLoading: isDictionariesLoading } =
    useGetDictionariesQuery();

  const [saveDrugBatch, { isLoading: isDrugLoading, isSuccess: isDrugSaveSuccess }] =
    usePersistMedicineBatchMutation();
  const [updatePackagesAmount] = useUpdatePackagesAmountMutation();
  const [deleteBatch, { isSuccess: isBatchDeleted }] = useDeleteBatchMutation();
  const [deleteDrug, { isSuccess: isDrugDeleted }] = useDeleteDrugMutation();

  const [getDrugInfo, { data: drugInfo, isLoading: isDrugInfoLoading }] =
    useLazyGetMedicineRecordQuery();

  const [
    saveExpenseBatch,
    { isSuccess: isExpenseSaveSuccess, isLoading: isExpenseSaving },
  ] = useExpenseBatchMutation();

  useEffect(() => {
    if (isDrugSaveSuccess) {
      setIsCreateModalOpen(false);
    }
    if (isDrugSaveSuccess && createBatchDrug != null) {
      setIsBatchCreateModalOpen(false);
      setCreateBatchDrug(null);
    }
  }, [isDrugSaveSuccess]);

  useEffect(() => {
    if (isExpenseSaveSuccess) {
      setExpenseModalOpen(false);
    }
  }, [isExpenseSaveSuccess]);

  useEffect(() => {
    if (isBatchDeleted) {
      setDeleteBatchModal(false);
    }
  }, [isBatchDeleted]);

  useEffect(() => {
    if (isDrugDeleted) {
      setDeleteDrugModal(false);
    }
  }, [isDrugDeleted]);

  const onDrugCreate = async (payload: any) => {
    await saveDrugBatch(payload);
  };

  const onBatchCreate = async (payload: any) => {
    await saveDrugBatch({ ...payload, drugId: createBatchDrug?.id });
  };

  const showDrugInfo = async (id: string) => {
    await getDrugInfo({ id });
    setIsDrugInfoModalOpen(true);
  };

  const onExpenseClick = async () => {
    const { data } = await getExpense({});
    setDrugsInternal(data?.drugs ?? []);
    setExpenseModalOpen(true);
  };

  const convertExpensedBatches = (data: any[]) => {
    return data?.flatMap((item: any) =>
      item?.batches?.map((batch: any) => ({
        drugBatchId: batch?.drugBatchId,
        usedPackages: batch?.usedPackages,
      })),
    );
  };

  const onExpenseConfirm = async () => {
    await saveExpenseBatch(convertExpensedBatches(drugsInternal));
  };

  const displayEditButton = hasPermission(auth, {
    permissions: pharmacy.monitoring.updateDrugAmount,
    level: OrganizationLevelType.ORGANIZATION,
  });

  return (
    <div className={'monitoring-page'}>
      {/*Добавить препарат*/}
      <Modal
        mode={'form'}
        schema={createModalConfig(dictionaries?.DRUG)}
        title={'Добавить препарат'}
        open={isCreateModalOpen}
        onSubmit={onDrugCreate}
        onCancel={() => {
          setIsCreateModalOpen(false);
        }}
        layout={{ formLayout: 'vertical', itemLayout: 'vertical' }}
        formKey={'createForm'}
        confirmLoading={isDrugLoading}
      />

      {/*Добавить партию*/}
      <Modal
        mode={'form'}
        title={`Добавить партию: ${createBatchDrug?.name}`}
        open={isBatchCreateModalOpen}
        schema={createBatchModalConfig}
        onSubmit={onBatchCreate}
        onCancel={() => {
          setIsBatchCreateModalOpen(false);
        }}
        layout={{ formLayout: 'vertical', itemLayout: 'vertical' }}
        formKey={'createBatchForm'}
        confirmLoading={isDrugLoading}
      />

      {/*Удалить партию*/}
      <Modal
        mode={'form'}
        open={deleteBatchModal}
        schema={deleteBatchModalConfig}
        width={'600px'}
        destroyOnHidden
        onCancel={() => {
          setDeleteBatchModal(false);
        }}
        onSubmit={async (payload: any) => {
          await deleteBatch({ ...payload, id: deleteBatchDrugId });
        }}
        layout={{ formLayout: 'vertical', itemLayout: 'vertical' }}
        text={
          'Вы уверены, что хотите удалить выбранные данные?\nПри выборе «Удалить» данные восстановлению не подлежат'
        }
        buttons={{
          submit: { text: 'Да' },
          cancel: { text: 'Нет' },
        }}
      />

      {/*Удалить препарат*/}
      <Modal
        mode={'form'}
        open={deleteDrugModal}
        schema={deleteBatchModalConfig}
        width={'600px'}
        destroyOnHidden
        onCancel={() => {
          setDeleteDrugModal(false);
        }}
        onSubmit={async (payload: any) => {
          await deleteDrug({ ...payload, id: deleteDrugId });
        }}
        layout={{ formLayout: 'vertical', itemLayout: 'vertical' }}
        text={
          'Вы уверены, что хотите удалить выбранные данные?\nПри выборе «Удалить» данные восстановлению не подлежат'
        }
        buttons={{
          submit: { text: 'Да' },
          cancel: { text: 'Нет' },
        }}
      />

      <AntModal
        loading={isDrugInfoLoading}
        className={'drugInfo-modal'}
        classNames={{ body: 'drugInfo-modal-content', content: 'drugInfo-modal-content' }}
        open={isDrugInfoModalOpen}
        footer={null}
        width={'80vw'}
        onCancel={() => {
          setIsDrugInfoModalOpen(false);
        }}>
        <Typography.Title level={3} className={'modal-title'}>
          {drugInfo?.name}
        </Typography.Title>
        <UTable
          size={'small'}
          columns={drugInfoColumns(dictionaries)}
          data={mapTableIndexWithPage(drugInfo?.tableParts)}
          loading={isDictionariesLoading}
        />
      </AntModal>

      <AntModal
        open={expenseModalOpen}
        width={'80vw'}
        onCancel={() => {
          setExpenseModalOpen(false);
        }}
        onOk={onExpenseConfirm}
        confirmLoading={isExpenseSaving}>
        <ExpenseModalContent
          loading={isExpenseLoading || isDictionariesLoading}
          drugs={drugsInternal}
          setDrugs={setDrugsInternal}
          tasks={expense?.taskDrugs}
          drugDictionary={dictionaries?.DRUG ?? []}
          displayEditButton
        />
      </AntModal>

      <PageTitle title={'Мониторинг срока годности и наличия ветеринарных препаратов'} />
      <div className={'action-buttons'}>
        <div>
          <Button
            type="primary"
            text="Добавить препарат"
            action={() => {
              setIsCreateModalOpen(true);
            }}
            display={hasPermission(auth, {
              permissions: pharmacy.monitoring.addDrug,
              level: OrganizationLevelType.ORGANIZATION,
            })}
          />
        </div>

        <div>
          <Button
            type="default"
            text="История изменений"
            className={'grouped-button'}
            action={() =>
              navigate(`/pharmacy/monitoring/history?organizationId=${orgId}`)
            }
            display={hasPermission(auth, { permissions: pharmacy.monitoring.viewDrugs })}
          />
          <Button
            type="primary"
            text="Списать препараты"
            disabled={!hasExpense}
            action={onExpenseClick}
            display={hasPermission(auth, {
              permissions: pharmacy.monitoring.expenseDrugs,
              level: OrganizationLevelType.ORGANIZATION,
            })}
          />
        </div>
      </div>
      <div className={'legenda'}>
        <p>
          Все параметры партии препарата в норме <span className={'color-white'} />{' '}
        </p>
        <p>
          Партия препарата с истекающим сроком годности{' '}
          <span className={'color-yellow'} />
        </p>
        <p>
          Партия препарата с истекшим сроком годности / остатком единиц тары ниже
          требуемого минимального остатка единиц тары <span className={'color-red'} />
        </p>
      </div>
      {drugs?.map((drug: any) => (
        <div>
          <AntCard
            size="small"
            variant="borderless"
            title={
              <div className={'title-wrapper'}>
                {dictionaries?.DRUG?.find((i: any) => i?.id === drug?.id)?.name}
                <Button
                  display={hasPermission(auth, {
                    permissions: pharmacy.monitoring.viewDrugInfo,
                  })}
                  shape={'circle'}
                  type={'text'}
                  onClick={() => showDrugInfo(drug?.id)}
                  icon={<InfoCircleOutlined />}></Button>
              </div>
            }
            extra={
              <>
                <Button
                  display={hasPermission(auth, {
                    permissions: pharmacy.monitoring.addDrugBatch,
                    level: OrganizationLevelType.ORGANIZATION,
                  })}
                  shape={'circle'}
                  type={'text'}
                  onClick={() => {
                    setCreateBatchDrug(
                      dictionaries?.DRUG?.find((i: any) => i?.id === drug?.id),
                    );
                    setIsBatchCreateModalOpen(true);
                  }}
                  icon={<PlusOutlined />}></Button>

                <Button
                  display={hasPermission(auth, {
                    permissions: pharmacy.monitoring.deleteDrug,
                    level: OrganizationLevelType.ORGANIZATION,
                  })}
                  shape={'circle'}
                  type={'text'}
                  onClick={() => {
                    setDeleteDrugId(drug?.id);
                    setDeleteDrugModal(true);
                  }}
                  icon={<DeleteOutlined />}></Button>
              </>
            }
            className={'drug-card'}
            classNames={{ header: 'drug-card-header', body: 'drug-card-body' }}>
            <UTable
              size={'small'}
              data={drug?.batches}
              columns={columns(
                updatePackagesAmount,
                drug?.batches?.length ?? 0,
                displayEditButton,
              )}
              loading={isLoading || isDictionariesLoading}
              rowClassName={(record: any) => {
                const classes: string[] = [];

                if (record?.packagesAmount < record?.minAmount) {
                  classes.push('row-warning-amount');
                }

                if (
                  dayjs(record?.expirationDate).isBefore(dayjs().add(1, 'month'), 'day')
                ) {
                  classes.push('row-warning');
                }

                if (dayjs(record?.expirationDate).isBefore(dayjs(), 'day')) {
                  classes.push('row-warning-expired');
                }

                return classes.join(' ');
              }}
              actions={() => [
                {
                  icon: <DeleteOutlined />,
                  action: (record: any) => {
                    setDeleteBatchDrugId(record?.id);
                    setDeleteBatchModal(true);
                  },
                  security: {
                    permissions: pharmacy.monitoring.deleteDrugBatch,
                    level: OrganizationLevelType.ORGANIZATION,
                  },
                },
              ]}
            />
          </AntCard>
        </div>
      ))}
    </div>
  );
});
